import { useCallback, useRef, useState } from 'react';
import { AutoComplete, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { notification } from '@ohpen/enso-ui';

import { addressesSearch, addressesGetDetails } from '@pmp/api';
import { getApiErrorMessage } from '@pmp/utils';
import { AddressLookupOption, AddressLookupProps } from './interfaces';

const MIN_QUERY_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 400;

export const AddressLookup: React.FC<AddressLookupProps> = ({ onAddressFound }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [options, setOptions] = useState<AddressLookupOption[]>([]);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const search = useDebouncedCallback((query: string) => {
    const requestId = ++requestIdRef.current;
    addressesSearch({ query: { query } })
      .then(({ data }) => {
        if (requestId !== requestIdRef.current) return;
        const suggestions: AddressLookupOption[] = (data.suggestions ?? []).map(s => ({
          value: s.globalAddressKey,
          label: s.text,
        }));
        if (!suggestions.length) {
          setOptions([{ value: '', label: t('firms.labels.findAddressNoResults'), disabled: true }]);
        } else if (data.moreResultsAvailable) {
          setOptions([...suggestions, { value: '', label: t('firms.labels.findAddressMoreResults'), disabled: true }]);
        } else {
          setOptions(suggestions);
        }
      })
      .catch(error => {
        if (requestId !== requestIdRef.current) return;
        setOptions([]);
        notification().error({ title: getApiErrorMessage(error) });
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, SEARCH_DEBOUNCE_MS);

  const onSearch = useCallback(
    (query: string) => {
      if (query.trim().length < MIN_QUERY_LENGTH) {
        search.cancel();
        requestIdRef.current++;
        setOptions([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      search(query.trim());
    },
    [search],
  );

  const onSelect = useCallback(
    (globalAddressKey: string) => {
      setValue('');
      setOptions([]);
      setLoading(true);
      addressesGetDetails({ body: { globalAddressKey } })
        .then(({ data }) => {
          onAddressFound(data);
        })
        .catch(error => {
          notification().error({ title: getApiErrorMessage(error) });
        })
        .finally(() => setLoading(false));
    },
    [onAddressFound],
  );

  return (
    <AutoComplete
      value={value}
      options={options}
      onChange={setValue}
      onSearch={onSearch}
      onSelect={onSelect}
      placeholder={t('firms.labels.findAddressPlaceholder')}
      notFoundContent={loading ? <Spin size="small" /> : null}
      allowClear
    />
  );
};
